import {
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';

import { CREATE_RETURNED_PENDING_STOCK_FROM_PRODUCT } from '../../../common/graphql/pendingStock';
import { GET_PRODUCT_FROM_BARCODE } from '../../../common/graphql/product';
import createClientOperationId from '../../../common/utils/createClientOperationId';
import { serializeDateKeyForTimezone } from '../../../common/utils/warehousing/formatExpiryBatchSummary';

import useBarcodeScanner from '../../hooks/useBarcodeScanner';

import {
	buildDefaultBatchRows,
	getBatchRowsQuantity,
	getFirstBatchExpiryDate,
	hasShortDatedBatchExpiryDate,
	normalizeBatchExpiryDatesForSave,
} from './PendingStockHelpers';

// Barcode lookup, batch rows and the create mutation behind the scan-excess dialog.
const usePendingStockExcessScan = ({
	item,
	timezone,
	canAddExcessStock,
	refetchList,
}) => {
	const [createReturnedPendingStockFromProduct, { loading: addExcessLoading }] = useMutation(CREATE_RETURNED_PENDING_STOCK_FROM_PRODUCT);
	const [getProductFromBarcode, { loading: productLookupLoading }] = useLazyQuery(GET_PRODUCT_FROM_BARCODE, {
		fetchPolicy: 'network-only',
	});

	const [isScanDialogOpen, setIsScanDialogOpen] = useState(false);
	const [scannedBarcode, setScannedBarcode] = useState('');
	const [scannedProduct, setScannedProduct] = useState(null);
	const [excessBatchRows, setExcessBatchRows] = useState(() => buildDefaultBatchRows(1));
	const excessCreationOperationRef = useRef(null);

	const normalizedExcessQuantity = useMemo(() => getBatchRowsQuantity(excessBatchRows), [excessBatchRows]);
	const isExcessShortDated = useMemo(() => (
		hasShortDatedBatchExpiryDate(excessBatchRows, timezone)
	), [excessBatchRows, timezone]);
	const canSubmitExcessStock = Boolean(scannedProduct?.id && normalizedExcessQuantity > 0);
	const warehouseProductForExcess = useMemo(() => ({
		product: scannedProduct,
	}), [scannedProduct]);

	const updateExcessBatchRow = useCallback((index, field, value) => {
		setExcessBatchRows(previous => previous.map((row, rowIndex) => (
			rowIndex === index ? { ...row, [field]: value } : row
		)));
	}, []);

	const addExcessBatchRow = useCallback(() => {
		setExcessBatchRows(previous => [...previous, ...buildDefaultBatchRows(null)]);
	}, []);

	const removeExcessBatchRow = useCallback((index) => {
		setExcessBatchRows(previous => (
			previous.length > 1 ? previous.filter((_, rowIndex) => rowIndex !== index) : previous
		));
	}, []);

	const resetScanState = useCallback(() => {
		excessCreationOperationRef.current = null;
		setScannedBarcode('');
		setScannedProduct(null);
		setExcessBatchRows(buildDefaultBatchRows(1));
	}, []);

	const closeScanDialog = useCallback(() => {
		setIsScanDialogOpen(false);
		resetScanState();
	}, [resetScanState]);

	const handleExcessBarcodeScanned = useCallback(async (barcode) => {
		setScannedBarcode(barcode);
		try {
			const response = await getProductFromBarcode({
				variables: {
					barcode,
				},
			});
			const product = response?.data?.getProductFromBarcode;
			if (!product?.id) {
				Toast.show('No product found for scanned barcode');
				return;
			}

			setScannedProduct(product);
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to look up scanned product');
		}
	}, [getProductFromBarcode]);

	const {
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		resetScanner,
		isScanning,
	} = useBarcodeScanner({
		onScanned: handleExcessBarcodeScanned,
	});

	const openScanDialog = useCallback(() => {
		if (!canAddExcessStock) {
			Toast.show('Select a returned item with a run and warehouse before adding excess stock');
			return;
		}

		resetScanState();
		resetScanner();
		setIsScanDialogOpen(true);
		requestCameraPermission();
	}, [canAddExcessStock, requestCameraPermission, resetScanState, resetScanner]);

	const scanAgain = useCallback(() => {
		resetScanState();
		resetScanner();
	}, [resetScanState, resetScanner]);

	const submitExcessStock = useCallback(async () => {
		if (!scannedProduct?.id || !item?.warehouse?.id || !item?.serviceRun?.id || normalizedExcessQuantity <= 0) {
			return;
		}

		const batchExpiryDates = normalizeBatchExpiryDatesForSave(excessBatchRows, timezone);
		const firstExpiryDate = getFirstBatchExpiryDate(excessBatchRows);
		const requestVariables = {
			warehouseId: item.warehouse.id,
			productId: scannedProduct.id,
			serviceRunId: item.serviceRun.id,
			quantity: normalizedExcessQuantity,
			expiryDate: serializeDateKeyForTimezone(firstExpiryDate, timezone),
			batchExpiryDates,
			note: scannedBarcode
				? `Excess returned stock scanned from barcode ${scannedBarcode}`
				: 'Excess returned stock scanned by picker',
		};
		const requestKey = JSON.stringify(requestVariables);
		if (excessCreationOperationRef.current?.requestKey !== requestKey) {
			excessCreationOperationRef.current = {
				requestKey,
				operationId: createClientOperationId('returned-stock'),
			};
		}

		try {
			await createReturnedPendingStockFromProduct({
				variables: {
					...requestVariables,
					operationId: excessCreationOperationRef.current.operationId,
				},
			});
			await refetchList?.();
			closeScanDialog();
			Toast.show('Excess returned stock added');
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to add excess returned stock');
		}
	}, [
		closeScanDialog,
		createReturnedPendingStockFromProduct,
		excessBatchRows,
		item?.serviceRun?.id,
		item?.warehouse?.id,
		normalizedExcessQuantity,
		refetchList,
		scannedBarcode,
		scannedProduct?.id,
		timezone,
	]);

	return {
		addExcessLoading,
		productLookupLoading,
		isScanDialogOpen,
		scannedBarcode,
		scannedProduct,
		excessBatchRows,
		updateExcessBatchRow,
		addExcessBatchRow,
		removeExcessBatchRow,
		normalizedExcessQuantity,
		isExcessShortDated,
		canSubmitExcessStock,
		warehouseProductForExcess,
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		isScanning,
		openScanDialog,
		closeScanDialog,
		scanAgain,
		submitExcessStock,
	};
};

export default usePendingStockExcessScan;
